import { useEffect, useState } from 'react';
import { connect } from 'react-redux';

import { isAssigned, triggerAssignment } from 'actions/ab';
import Agreement from 'components/landing/Agreement';
import BrandReviews from 'components/landing/BrandReviews';
import BrandTrending from 'components/landing/BrandTrending';
import BrokenPage from 'components/error/BrokenPage';
import Departments from 'components/landing/Departments';
import EventCallout from 'components/landing/EventCallout';
import Faqs from 'components/landing/Faqs';
import GenericBrandFacets from 'components/landing/GenericBrandFacets';
import HeroFancy from 'components/landing/HeroFancy';
import Images from 'components/landing/Images';
import Iframe from 'components/landing/Iframe';
import Instagram from 'components/landing/Instagram';
import MelodyArticleImages from 'components/landing/MelodyArticleImages';
import MelodyArticleImageCopy from 'components/landing/MelodyArticleImageCopy';
import MelodyBrandIndex from 'components/landing/MelodyBrandIndex';
import MelodyCategory from 'components/landing/MelodyCategory';
import MelodyEditorialPromo from 'components/landing/MelodyEditorialPromo';
import MelodyHeaderRule from 'components/landing/MelodyHeaderRule';
import MelodyHero from 'components/landing/MelodyHero';
import MelodyHeroFull from 'components/landing/MelodyHeroFull';
import MelodyHorizontalNav from 'components/landing/MelodyHorizontalNav';
import MelodyPersonalizedBrand from 'components/landing/MelodyPersonalizedBrand';
import MelodyPersonalizedCategories from 'components/landing/MelodyPersonalizedCategories';
import MelodyPromoGroup from 'components/landing/MelodyPromoGroup';
import MelodySizingGuide from 'components/landing/MelodySizingGuide';
import MelodySplitEditorial from 'components/landing/MelodySplitEditorial';
import MelodyVideoPlayer from 'components/common/melodyVideo/MelodyVideoPlayer';
import MelodyNewsfeed from 'components/landing/newsfeed/MelodyNewsfeed';
import NotificationSignup from 'components/landing/NotificationSignup';
import PageContent from 'components/landing/PageContent';
import DynamicPageContent from 'containers/landing/DynamicPageContent';
import ProductSearch from 'components/landing/ProductSearch';
import Raffle from 'components/landing/Raffle';
import Recommender from 'components/landing/Recommender';
import ReleaseCalendar from 'components/landing/ReleaseCalendar';
import TwoWideBlocks from 'components/landing/TwoWideBlocks';
import ZapposHero from 'components/landing/ZapposHero';
import ShopTheLook from 'containers/landing/ShopTheLook';

// The keys here map directly to the component name in content symphony.
const slotContentTypes = {
  'agreement': Agreement,
  'brandNotification': NotificationSignup,
  'BrokenPage': BrokenPage,
  'departments': Departments,
  'DynamicPageContent': DynamicPageContent,
  'eventCallout': EventCallout,
  'faqs': Faqs,
  'genericBrandAbout': PageContent, // reusing PageContent for Taxonomy pages.
  'genericBrandEmails': NotificationSignup, // reusing Notifications for Taxonomy pages
  'genericBrandFacets': GenericBrandFacets,
  'genericBrandReviews': BrandReviews,
  'genericBrandTrending': BrandTrending,
  'heroFancy': HeroFancy,
  'iframe': Iframe,
  'images': Images,
  'instagram': Instagram, // Gateway component name
  'catskillsInstagram': Instagram, // ZCS component name
  'melodyArticleImages': MelodyArticleImages,
  'melodyArticleImageCopy': MelodyArticleImageCopy,
  'melodyBrandIndex': MelodyBrandIndex,
  'melodyCategory': MelodyCategory,
  'melodyEditorialPromo': MelodyEditorialPromo,
  'melodyHeaderRule': MelodyHeaderRule,
  'melodyHero': MelodyHero,
  'melodyHeroFull': MelodyHeroFull,
  'melodyHorizontalNav': MelodyHorizontalNav,
  'melodyPersonalizedBrand': MelodyPersonalizedBrand,
  'melodyPersonalizedCategories': MelodyPersonalizedCategories,
  'melodyPromoGroup': MelodyPromoGroup,
  'melodySizingGuide': MelodySizingGuide,
  'melodySplitEditorial': MelodySplitEditorial,
  'melodyVideoPlayer': MelodyVideoPlayer,
  'melodyNewsFeed': MelodyNewsfeed,
  'pageContent': PageContent,
  'productSearch': ProductSearch,
  'raffle': Raffle,
  'recommender': Recommender,
  'releaseCalendar': ReleaseCalendar,
  'shopTheLook': ShopTheLook,
  'twoWideBlocks': TwoWideBlocks,
  'zapposHero': ZapposHero
};

export const LandingSlot = props => {
  const {
    isRecognized,
    isInSymphonyAssignment,
    triggerAssignment,
    slotName,
    data = {},
    onTaxonomyComponentClick,
    onLandingComponentClick,
    shouldLazyLoad,
    slotHeartsData,
    ipStatus,
    slotContentTypesList = slotContentTypes,
    slotIndex
  } = props;

  const { componentName, testName, testTreatment, testTrigger } = data;

  useEffect(() => {
    const validTest = typeof testName === 'string' && Array.isArray(testTrigger) && typeof testTreatment === 'object';
    const testUnrecognized = testTrigger?.includes('UnrecognizedCustomer') && !isRecognized;
    const testRecognized = testTrigger?.includes('RecognizedCustomer') && isRecognized;
    const testRecognizedCustomerPreveouslyOptedIn = testTrigger?.includes('RecognizedCustomerPreveouslyOptedIn') && isInSymphonyAssignment && isRecognized;

    if (validTest && (testUnrecognized || testRecognized || testRecognizedCustomerPreveouslyOptedIn)) {
      const { index } = triggerAssignment(testName);
      setAssignmentGroup(index);
    }
  }, [isInSymphonyAssignment, isRecognized, testName, testTreatment, testTrigger, triggerAssignment]);

  const [assignmentGroup, setAssignmentGroup] = useState(null);

  if (!componentName) {
    return null;
  }

  const SlotContent = slotContentTypesList[componentName];

  const renderSlot = () => {
    if (assignmentGroup !== null) {
      return testTreatment[assignmentGroup] === 'Render';
    }
    return !testName && assignmentGroup === null; // Prevents an initial truthy render.
  };

  return SlotContent && renderSlot() ?
    <SlotContent
      slotName={slotName}
      slotDetails={data}
      slotIndex={slotIndex}
      onLandingComponentClick={onLandingComponentClick}
      onTaxonomyComponentClick={onTaxonomyComponentClick}
      shouldLazyLoad={shouldLazyLoad}
      slotHeartsData={slotHeartsData}
      ipStatus={ipStatus}
    /> : null;
};

const mapStateToProps = (state, ownProps) => ({
  isRecognized: !!state.cookies['x-main'],
  isInSymphonyAssignment: !!isAssigned(ownProps.data?.testName, 1, state)
});

const mapDispatchToProps = {
  triggerAssignment
};

export default connect(mapStateToProps, mapDispatchToProps)(LandingSlot);
